/**
 * Rating-balanced team splits for the Fair teams tool.
 *
 * Team strength follows the app's team model (the backend's TrueSkillCalculator._virtual_player):
 * a team is as strong as its best player, plus 10% per extra teammate. Because that ignores
 * everyone but the leader, splits are also nudged towards equal average mu, so two splits with
 * the same model strength prefer the one that also feels fair at the table.
 */

/** Same bonus as the backend team model: strongest mu x (1 + 0.1 per extra teammate). */
export const LEADER_BONUS = 0.1;

/** Weight of the average-mu gap next to the team-model gap. */
const MEAN_WEIGHT = 0.5;

/** Splits within this cost of the best one count as equally fair (Shuffle picks among them). */
const FAIR_TOLERANCE = 0.75;

export interface RatedPlayer {
  id: string;
  name: string;
  /** TrueSkill mu */
  mu: number;
}

/** Strength of a team from its players' mu values (0 for an empty team). */
export const teamStrength = (mus: number[]): number =>
  mus.length === 0 ? 0 : Math.max(...mus) * (1 + LEADER_BONUS * (mus.length - 1));

/** Team sizes for n players in k teams, as even as possible (bigger teams first). */
export const teamSizes = (playerCount: number, teamCount: number): number[] =>
  Array.from({ length: teamCount }, (_, i) =>
    Math.floor(playerCount / teamCount) + (i < playerCount % teamCount ? 1 : 0),
  );

const spread = (values: number[]): number => Math.max(...values) - Math.min(...values);

/** How unfair a split is: 0 means every team is equally strong. */
export const splitCost = (teams: string[][], muById: Record<string, number>): number => {
  const mus = teams.map(team => team.map(id => muById[id]));
  const strengths = mus.map(teamStrength);
  const means = mus.map(m => (m.length ? m.reduce((a, b) => a + b, 0) / m.length : 0));
  return spread(strengths) + MEAN_WEIGHT * spread(means);
};

/** Order-independent key of a split, to spot duplicates. */
export const splitKey = (teams: string[][]): string =>
  teams
    .map(team => [...team].sort().join(','))
    .sort()
    .join('|');

const shuffled = <T,>(items: T[], rng: () => number): T[] => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/** A random split with even sizes; locked players start (and stay) in their team. */
const randomSplit = (
  players: RatedPlayer[],
  teamCount: number,
  locks: Record<string, number>,
  rng: () => number,
): string[][] => {
  const teams: string[][] = Array.from({ length: teamCount }, () => []);
  const free: string[] = [];
  players.forEach(p => {
    const lock = locks[p.id];
    if (lock !== undefined && lock >= 0 && lock < teamCount) teams[lock].push(p.id);
    else free.push(p.id);
  });
  const sizes = teamSizes(players.length, teamCount);
  shuffled(free, rng).forEach(id => {
    // Fill the team furthest below its target size (random among ties)
    const rooms = teams.map((t, i) => sizes[i] - t.length);
    const best = Math.max(...rooms);
    const candidates = rooms.map((r, i) => (r === best ? i : -1)).filter(i => i >= 0);
    teams[candidates[Math.floor(rng() * candidates.length)]].push(id);
  });
  return teams;
};

/** Swaps unlocked players between teams while that makes the split fairer. */
const improve = (teams: string[][], muById: Record<string, number>, locked: Set<string>): string[][] => {
  let current = teams.map(t => [...t]);
  let cost = splitCost(current, muById);
  for (;;) {
    let bestSwap: string[][] | null = null;
    let bestCost = cost - 1e-9;
    for (let a = 0; a < current.length; a++) {
      for (let b = a + 1; b < current.length; b++) {
        for (let i = 0; i < current[a].length; i++) {
          if (locked.has(current[a][i])) continue;
          for (let j = 0; j < current[b].length; j++) {
            if (locked.has(current[b][j])) continue;
            const next = current.map(t => [...t]);
            [next[a][i], next[b][j]] = [current[b][j], current[a][i]];
            const nextCost = splitCost(next, muById);
            if (nextCost < bestCost) {
              bestCost = nextCost;
              bestSwap = next;
            }
          }
        }
      }
    }
    if (!bestSwap) return current;
    current = bestSwap;
    cost = bestCost;
  }
};

export interface BalanceOptions {
  /** Players locked to a team index stay there. */
  locks?: Record<string, number>;
  /** Avoid returning this split (its splitKey) when another fair one exists: "Shuffle again". */
  avoidKey?: string;
  rng?: () => number;
  restarts?: number;
}

/**
 * Splits players into teamCount rating-balanced teams (arrays of player IDs, by team index).
 * Picks at random among the fairest splits found, so calling it again shuffles.
 */
export const balanceTeams = (
  players: RatedPlayer[],
  teamCount: number,
  { locks = {}, avoidKey, rng = Math.random, restarts = 40 }: BalanceOptions = {},
): string[][] => {
  if (teamCount < 1) return [];
  const muById = Object.fromEntries(players.map(p => [p.id, p.mu]));
  const locked = new Set(
    Object.keys(locks).filter(id => muById[id] !== undefined && locks[id] >= 0 && locks[id] < teamCount),
  );

  const found = new Map<string, { teams: string[][]; cost: number }>();
  for (let r = 0; r < restarts; r++) {
    const teams = improve(randomSplit(players, teamCount, locks, rng), muById, locked);
    const key = splitKey(teams);
    if (!found.has(key)) found.set(key, { teams, cost: splitCost(teams, muById) });
  }

  const results = Array.from(found.entries());
  const bestCost = Math.min(...results.map(([, v]) => v.cost));
  let fair = results.filter(([, v]) => v.cost <= bestCost + FAIR_TOLERANCE);
  if (avoidKey && fair.length > 1) fair = fair.filter(([key]) => key !== avoidKey);
  return fair[Math.floor(rng() * fair.length)][1].teams;
};

/** Deterministic random numbers for tests and reproducible shuffles. */
export const seededRng = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
